/** Ekip on the phone (artboard F_Ekip): live agent rows, the program and today's handoffs. */

import type { Snapshot } from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { agentCounts, teamHeadline, teamSubline } from '../app/selectors.js';
import { Icon } from '../components/icons.js';
import { OrganizationToggle } from '../components/organization.js';
import { Meter, SectionHeader } from '../components/primitives.js';
import { AppBar, BottomTabBar, StatusArea } from '../components/shell.js';
import { AgentRow, HandoffList, ProgramRuler } from '../components/team.js';
import { formatLongDate, formatPct, formatQuotaPct, formatUsdPair, monthProgress } from '../lib/format.js';
import { Loading } from './common.js';

export function PauseAllButton({ desktop = false }: { desktop?: boolean }) {
  const store = useStore();
  const { team } = usePanel();
  const disabled = team.mode !== 'running';
  return (
    <button
      type="button"
      className={desktop ? 'pause-all pause-all--desktop' : 'pause-all'}
      aria-label="Tümünü duraklat: her ajan mevcut adımını bitirip durur, kuyruk korunur, geri alınabilir"
      disabled={disabled}
      onClick={() => void store.pauseTeam()}
    >
      <span className="pause-all__box">
        <Icon name="pause" size={16} />
        {desktop ? (
          <span className="pause-all__text">
            <span className="pause-all__label">Tümünü duraklat</span>
            <span className="key ink-3">Adımını bitirir</span>
          </span>
        ) : (
          'Tümünü duraklat'
        )}
      </span>
    </button>
  );
}

function TeamSummary({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const c = agentCounts(snapshot.agents);
  const ratio = snapshot.genesis.spent / snapshot.genesis.cap;
  const tick = monthProgress(now);
  const gm = snapshot.agents.find((agent) => agent.budget.kind === 'claude_max');
  return (
    <section className="team-summary drafting" aria-label="Ekip özeti">
      <div className="team-summary__top">
        <div className="team-summary__titles">
          <h1 className="headline__title" aria-live="polite">
            {teamHeadline(c)}
          </h1>
          <p className="headline__sub" aria-live="polite">
            {teamSubline(c)}
          </p>
        </div>
        <PauseAllButton />
      </div>
      <dl className="team-summary__budgets">
        <dt>Genesis · {snapshot.store.monthLabel}</dt>
        <dd className="team-summary__genesis">
          <span className="mono team-summary__money">
            {formatUsdPair(snapshot.genesis.spent, snapshot.genesis.cap)}
          </span>
          <Meter
            value={ratio}
            tick={tick}
            height={4}
            pace
            className="team-summary__meter"
            label={`Genesis bu ayki harcaması ${formatUsdPair(snapshot.genesis.spent, snapshot.genesis.cap)}, tavanın yüzde ${Math.round(ratio * 100)}'si; ayın yüzde ${Math.round(tick * 100)}'ı geçti`}
          />
          <span className="nowrap">
            <span className="mono">{formatPct(ratio)}</span> · ay <span className="mono">{formatPct(tick)}</span>
          </span>
        </dd>
        {gm && gm.budget.kind === 'claude_max' ? (
          <>
            <dt>GM aboneliği</dt>
            <dd className="team-summary__quota">
              <span className="mono team-summary__strong">5 sa {formatQuotaPct(gm.budget.window5hPct)}</span> · hafta{' '}
              <span className="mono team-summary__strong">{formatQuotaPct(gm.budget.weekPct)}</span>
            </dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

export function Ekip({ run = null }: { run?: number | null }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const subtitle = formatLongDate(state.now);
  if (!snapshot) {
    return (
      <>
        <AppBar title="Ekip" subtitle={subtitle} />
        <Loading />
        <BottomTabBar active="ekip" />
      </>
    );
  }
  const now = state.now;
  return (
    <>
      <AppBar title="Ekip" subtitle={subtitle} />
      <StatusArea to="/kayitlar" target="Kayıtları aç" />
      <TeamSummary snapshot={snapshot} now={now} />
      <main className="phone-main">
        <ul className="agents" aria-label="Ajanlar">
          {snapshot.agents.map((agent) => (
            <AgentRow key={agent.id} agent={agent} now={now} />
          ))}
        </ul>
        <section aria-labelledby="ekip-program" className="section">
          <SectionHeader
            id="ekip-program"
            title="Program"
            right={<span className="section-header__aside">bugün</span>}
          />
          <ProgramRuler schedule={snapshot.schedule} now={now} />
          <p className="ruler__foot">Ajanlar iş gelince çalışır; aradaki sessizlik normal.</p>
        </section>
        <section aria-labelledby="ekip-devirler" className="section section--tight">
          <SectionHeader
            id="ekip-devirler"
            title="Bugünkü devirler"
            count={snapshot.handoffs.length}
            action={{ label: 'Kayıtlar', to: '/kayitlar' }}
          />
          <HandoffList handoffs={snapshot.handoffs} runs={snapshot.runs} now={now} focusRun={run} />
        </section>
        <OrganizationToggle />
      </main>
      <BottomTabBar active="ekip" />
    </>
  );
}
